import { resolveApiOrigin } from '../config/apiBase.js'

/**
 * Server-Sent Events (SSE) Service
 * Handles real-time communication with the backend SSE endpoint
 */

class SSEService {
  constructor() {
    this.eventSource = null
    this.isConnected = false
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = 5
    this.reconnectInterval = 5000 // 5 seconds
    this.listeners = new Map()
    this.connectionStatusListeners = []
  }

  /**
   * Connect to SSE stream
   * @param {string} baseUrl - Base URL for the API (optional, defaults to VITE_API_URL from environment)
   */
  connect(baseUrl = null) {
    // Prevent multiple simultaneous connections
    if (this.eventSource && (this.eventSource.readyState === EventSource.OPEN || this.eventSource.readyState === EventSource.CONNECTING)) {
      console.log('SSE: Already connected or connecting')
      return Promise.resolve()
    }

    // Clean up any existing connection
    if (this.eventSource) {
      this.eventSource.close()
      this.eventSource = null
    }

    const url = baseUrl || resolveApiOrigin()
    const sseUrl = `${url}/api/sse-stream`
    
    console.log('SSE: Connecting to', sseUrl)
    
    return new Promise((resolve, reject) => {
      let connectionTimeout
      
      try {
        this.eventSource = new EventSource(sseUrl)
        
        // Set a connection timeout
        connectionTimeout = setTimeout(() => {
          if (this.eventSource && this.eventSource.readyState === EventSource.CONNECTING) {
            console.error('SSE: Connection timeout')
            this.eventSource.close()
            reject(new Error('SSE connection timeout'))
          }
        }, 10000) // 10 second timeout
        
        this.eventSource.onopen = (event) => {
          console.log('SSE: Connection opened')
          clearTimeout(connectionTimeout)
          this.isConnected = true
          this.reconnectAttempts = 0
          this.notifyConnectionStatus(true)
          resolve()
        }

        this.eventSource.onmessage = (event) => {
          try {
            // Check if the data looks like HTML (common error response)
            if (event.data.trim().startsWith('<!DOCTYPE html>') || event.data.trim().startsWith('<html')) {
              console.error('SSE: Received HTML response instead of JSON:', event.data.substring(0, 200) + '...')
              this.eventSource.close()
              reject(new Error('SSE endpoint returned HTML instead of JSON'))
              return
            }
            
            const data = JSON.parse(event.data)
            console.log('SSE: Raw message received:', data)
            this.handleMessage(data, event.lastEventId)
          } catch (error) {
            console.error('SSE: Error parsing message:', error)
            console.error('SSE: Raw data:', event.data)
            
            // If it's HTML, close the connection
            if (event.data.includes('<!DOCTYPE html>') || event.data.includes('<html')) {
              console.error('SSE: HTML response detected, closing connection')
              this.eventSource.close()
              reject(new Error('SSE endpoint returned HTML response'))
            }
          }
        }

        this.eventSource.onerror = (event) => {
          console.error('SSE: Connection error:', event)
          console.error('SSE: Ready state:', this.eventSource?.readyState)
          console.error('SSE: URL:', sseUrl)
          
          clearTimeout(connectionTimeout)
          
          const readyState = this.eventSource?.readyState
          
          // Only mark as disconnected if the connection is actually closed
          if (readyState === EventSource.CLOSED) {
            this.isConnected = false
            this.notifyConnectionStatus(false)
            console.log('SSE: Connection closed, attempting reconnection...')
            this.handleReconnect()
          } else if (readyState === EventSource.CONNECTING) {
            console.log('SSE: Connection in progress, waiting...')
            // Don't mark as disconnected if still connecting
          } else {
            console.log('SSE: Connection error but still open, monitoring...')
            // Connection might be temporarily interrupted but not closed
          }
          
          // Only reject if this is the initial connection attempt and connection is closed
          if (resolve && reject && readyState === EventSource.CLOSED) {
            reject(new Error('SSE connection failed'))
          }
        }

        // Note: We handle all messages through onmessage above
        // Specific event types (connection, heartbeat) will also come through onmessage

      } catch (error) {
        console.error('SSE: Failed to create EventSource:', error)
        reject(error)
      }
    })
  }

  /**
   * Handle incoming SSE messages
   * @param {Object} data - Parsed message data
   * @param {string} eventId - Event ID for tracking
   */
  handleMessage(data, eventId) {
    console.log('SSE: Message received:', data)
    
    // Notify all listeners for this specific message type
    const typeListeners = this.listeners.get(data.type) || []
    typeListeners.forEach(callback => {
      try {
        callback(data, eventId)
      } catch (error) {
        console.error('SSE: Error in message handler for type', data.type, ':', error)
      }
    })

    // Also notify general message listeners (wildcard *)
    const generalListeners = this.listeners.get('*') || []
    generalListeners.forEach(callback => {
      try {
        callback(data, eventId)
      } catch (error) {
        console.error('SSE: Error in general message handler:', error)
      }
    })
  }

  /**
   * Handle reconnection logic
   */
  handleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('SSE: Max reconnection attempts reached')
      return
    }

    this.reconnectAttempts++
    console.log(`SSE: Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})`)
    
    setTimeout(() => {
      this.disconnect()
      // Use exponential backoff for reconnection
      const backoffDelay = Math.min(this.reconnectInterval * Math.pow(2, this.reconnectAttempts - 1), 30000)
      setTimeout(() => {
        this.connect().catch(error => {
          console.error('SSE: Reconnection failed:', error)
        })
      }, backoffDelay)
    }, this.reconnectInterval)
  }

  /**
   * Subscribe to specific message types
   * @param {string} type - Message type to listen for ('*' for all)
   * @param {Function} callback - Callback function to handle messages
   * @returns {Function} Unsubscribe function
   */
  subscribe(type, callback) {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, [])
    }
    
    this.listeners.get(type).push(callback)
    
    // Return unsubscribe function
    return () => {
      const listeners = this.listeners.get(type)
      if (listeners) {
        const index = listeners.indexOf(callback)
        if (index > -1) {
          listeners.splice(index, 1)
        }
      }
    }
  }

  /**
   * Subscribe to connection status changes
   * @param {Function} callback - Callback function for status changes
   * @returns {Function} Unsubscribe function
   */
  onConnectionStatus(callback) {
    this.connectionStatusListeners.push(callback)
    
    return () => {
      const index = this.connectionStatusListeners.indexOf(callback)
      if (index > -1) {
        this.connectionStatusListeners.splice(index, 1)
      }
    }
  }

  /**
   * Notify connection status listeners
   * @param {boolean} connected - Connection status
   */
  notifyConnectionStatus(connected) {
    this.connectionStatusListeners.forEach(callback => {
      try {
        callback(connected)
      } catch (error) {
        console.error('SSE: Error in connection status handler:', error)
      }
    })
  }

  /**
   * Disconnect from SSE stream
   */
  disconnect() {
    if (this.eventSource) {
      console.log('SSE: Disconnecting')
      this.eventSource.close()
      this.eventSource = null
    }
    this.isConnected = false
    this.reconnectAttempts = 0
    this.notifyConnectionStatus(false)
  }

  /**
   * Get current connection status
   * @returns {boolean} True if connected
   */
  getConnectionStatus() {
    return this.isConnected && this.eventSource && this.eventSource.readyState === EventSource.OPEN
  }

  /**
   * Publish a message to the SSE stream (via API)
   * @param {Object} messageData - Message data to publish
   * @param {string} baseUrl - Base URL for the API (optional, defaults to VITE_API_URL from environment)
   * @returns {Promise} API response
   */
  async publishMessage(messageData, baseUrl = null) {
    try {
      const url = baseUrl || resolveApiOrigin()
      const publishUrl = `${url}/api/sse-publish`
      
      const response = await fetch(publishUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(messageData)
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const result = await response.json()
      console.log('SSE: Message published:', result)
      return result
    } catch (error) {
      console.error('SSE: Failed to publish message:', error)
      throw error
    }
  }
}

// Create and export a singleton instance
export const sseService = new SSEService()

// Export the class for testing or multiple instances
export default SSEService
